/**
 * Created by jonas on 2020-10-01.
 */

import {gsap} from "gsap";
import {APIEventSystem} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {
    Positionable,
    SimpleReelModel,
    SimpleReelModelDelegate,
    SimpleReelModelEventData,
    SimpleReelModelState,
    SimpleReelModelSymbolData,
    SimpleSymbolState
} from "./SimpleReelModel";
import {BS2Statics} from "../../../game/config/BS2Statics";
import {GSAPUtils} from "../../../game/utils/GSAPUtils";
import {replaceWithNewSymbolDataType} from "../../../game/server/BS2Data";
import {BS2Symbol} from "../../../game/reels/BS2Symbol";
import {BS2Sound} from "../../../game/config/sound/BS2Sound";
import {BS2} from "../../../game/BS2";


export interface SimpleReelPaddingData {
    visualPaddingBefore: number;
    paddingBefore: number;
    visibleSymbols: number;
    paddingAfter: number;
    visualPaddingAfter: number;
}

export interface SymbolStopCallBackData {
    isNearWin: boolean;
    isSingleSymbolDrop: boolean;
}

interface AvalancheSymbolLandCb {
    (symbolData: SimpleReelModelSymbolData, data: SymbolStopCallBackData): void;
}

export class SimpleSpinReelModel extends SimpleReelModel {
    public illegalVisualPaddingSymbols: string[] = [];
    public active: boolean = true;
    public collectable: boolean = true;
    public paddingConfig: SimpleReelPaddingData = {
        visualPaddingBefore: 1,
        paddingBefore: 0,
        visibleSymbols: 5,
        paddingAfter: 0,
        visualPaddingAfter: 1,
    }
    public animatedStepData: { x: number, y: number } = {
        x: 0,
        y: 0
    }
    public stepSize: { x: number, y: number } = {
        x: 180,
        y: 180
    };
    public stepDuration: number = 0.1;
    public readonly id: number;
    public _delegate: SimpleReelModelDelegate;
    /**
     * Triggers on: ReelStateChange and each ReelModelState
     *
     *
     */
    public readonly events: APIEventSystem | undefined;
    private _triggerTime: number;
    private _stopTriggerTl: gsap.core.Timeline | undefined;
    //Fake reel
    private _reelSet: string[] | undefined;
    private _reelSetIndex: number = 0;
    private _stopIndex: number;
    private _stopSymbols: SimpleReelModelSymbolData[] = [];
    private _symbols: SimpleReelModelSymbolData[] = [];
    private _spinning: boolean = false;
    private _currentSpinStep: gsap.core.Tween | undefined;
    private animationTimeScale = 1;
    private _stopTimeline: gsap.core.Timeline | undefined;
    private _stopping: boolean = false;
    private triggerStop: boolean = false;
    private _startedReverse: boolean;
    private _speed: number = this.animationTimeScale / this.stepDuration;
    private masterSpinTimeline: gsap.core.Timeline | undefined;
    private _state: SimpleReelModelState = SimpleReelModelState.IDLE;

    constructor(id: number, delegate: SimpleReelModelDelegate, triggerEvents: boolean = false) {
        super(id, delegate, triggerEvents)
    }

    public setReelSet(reelSet: string[], index?: number) {
        this._reelSet = reelSet;
        index = index != undefined ? index : Math.floor(Math.random() * this._reelSet.length);
        this.setReelIndex(index);
    }

    public init(symbols: string[]) {
        this.clearSymbols(this._symbols);
        this.clearSymbols(this._stopSymbols);

        const totalCount = this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore + this.paddingConfig.visibleSymbols + this.paddingConfig.paddingAfter + this.paddingConfig.visualPaddingAfter;
        const totalInitCount = this.paddingConfig.paddingBefore + this.paddingConfig.visibleSymbols + this.paddingConfig.paddingAfter;

        if (symbols.length != totalInitCount) {
            Logger.warn(`ReelModel.init:  Symbol count mismatch in reel ${this.id}`);
        }

        symbols = this.getRandomSymbols(this.paddingConfig.visualPaddingBefore).concat(symbols);
        symbols = symbols.concat(this.getRandomSymbols(this.paddingConfig.visualPaddingAfter));

        for (let i: number = 0; i < symbols.length; i++) {
            const symbol = this.createNewSymbol(symbols[i], i - this.paddingConfig.visualPaddingBefore - this.paddingConfig.paddingBefore, SimpleReelModelState.IDLE);
            this._symbols.push(symbol);
        }

        this.updatePosition();
    }

    public halt() {
        if (this._currentSpinStep) {
            this.masterSpinTimeline?.kill();
            this._currentSpinStep?.progress(1);
        }
    }

    public resume() {
        this.startSpin();
    }

    public stop(stopSymbolNames: string[], reverse: boolean = false): gsap.core.Timeline {
        reverse = this._startedReverse;
        if (this.state !== SimpleReelModelState.START && this.state !== SimpleReelModelState.SPIN) {
            return gsap.timeline();
        }

        this.clearSymbols(this._stopSymbols);
        this._stopSymbols = [];
        const numberOfNormalSteps: number = this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore + this.paddingConfig.visibleSymbols + this.paddingConfig.paddingAfter + this.paddingConfig.visualPaddingAfter;

        stopSymbolNames = this.getRandomSymbols(this.paddingConfig.visualPaddingBefore).concat(stopSymbolNames);
        stopSymbolNames = stopSymbolNames.concat(this.getRandomSymbols(this.paddingConfig.visualPaddingAfter));

        for (let i: number = 0; i < stopSymbolNames.length; i++) {
            const symbol: SimpleReelModelSymbolData = this.createNewSymbol(stopSymbolNames[i], i - this.paddingConfig.visualPaddingBefore - this.paddingConfig.paddingBefore, SimpleReelModelState.STOP);
            symbol.positionable.position.y = this.stepSize.y * -(this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore + this.paddingConfig.visibleSymbols);
            this._stopSymbols.push(symbol);
        }

        this._stopTriggerTl = gsap.timeline({
            paused: true,
            onStart: () => {
                this.state = SimpleReelModelState.STOP;
                this._spinning = false;
                this.triggerStop = true;
                this._triggerTime = Date.now();
            }
        });

        this._stopTimeline = gsap.timeline({
            paused: true,
            onStart: () => {
                //  console.log(Date.now() - this._triggerTime);
                this._stopIndex = stopSymbolNames.length - 1;
                this._stopping = true;
            },
            onComplete: () => {
                this._stopTimeline = undefined;
                this._stopSymbols = [];
                this._stopping = false;
                this.state = SimpleReelModelState.STOPPED;
                this.state = SimpleReelModelState.IDLE;
            }
        });


        for (let i: number = 0; i < numberOfNormalSteps; i++) {
            const tween = this.step(reverse);
            this._stopTimeline.add(tween);
        }

        this._stopTimeline.add(this.bounce(0.1, 0.5));

        this._stopTriggerTl.add(gsap.to(this, {duration: this._stopTimeline.totalDuration()}));

        return this._stopTriggerTl.play(0);
    }

    public start(reverse: boolean = false): gsap.core.Timeline {
        this._startedReverse = reverse;
        const tl: gsap.core.Timeline = gsap.timeline({
            onStart: () => {
                this.state = SimpleReelModelState.START;
                this._stopSymbols = [];
            },
            onComplete: () => {
                this.startSpin(reverse);
            }
        });

        /*
                const tween = this.step(reverse);
                tween.vars.ease = "back.in(3)";
                tween.totalDuration(this.stepDuration * 8);
        */

        const tween = this.windUp(0.4, reverse);

        tl.add(tween);

        return tl;
    }

    public windUp(windFactor: number = 0.2, reverse: boolean = false): gsap.core.Timeline {
        const tl = gsap.timeline();
        const wind = gsap.to(this.animatedStepData, {
            duration: this.stepDuration * 8,
            x: reverse ? this.stepSize.x * windFactor : -this.stepSize.x * windFactor,
            y: reverse ? this.stepSize.y * windFactor : -this.stepSize.y * windFactor,
            ease: "power2.inOut",
            onStart: () => {
                this.state = SimpleReelModelState.WIND_UP;
            },
            onUpdate: () => this.updatePosition(),
        });

        const back = gsap.to(this.animatedStepData, {
            duration: this.stepDuration * 6,
            x: 0,
            y: 0,
            ease: "power2.in",
            onUpdate: () => this.updatePosition(),
        });

        tl.add(wind);
        tl.add(back);

        return tl;
    }

    public bounce(bounceFactor: number = 0.2, durationFactor: number = 1, reverse: boolean = false): gsap.core.Timeline {
        const tl = gsap.timeline();
        const overshoot = gsap.to(this.animatedStepData, {
            duration: this.stepDuration * durationFactor,
            x: reverse ? -this.stepSize.x * bounceFactor : this.stepSize.x * bounceFactor,
            y: reverse ? -this.stepSize.y * bounceFactor : this.stepSize.y * bounceFactor,
            ease: "power2.out",
            onStart: () => {
                this.state = SimpleReelModelState.BOUNCE;
            },
            onUpdate: () => this.updatePosition(),
        });

        const back = gsap.to(this.animatedStepData, {
            duration: this.stepDuration * durationFactor * 2,
            x: 0,
            y: 0,
            ease: "power2.out",
            onUpdate: () => this.updatePosition(),
        });

        tl.add(overshoot);
        tl.add(back);

        return tl;
    }

    public nudge(reverse: boolean = false): gsap.core.Tween {
        const tween = this.step(reverse);
        tween.eventCallback("onStart", () => {
            this.state = SimpleReelModelState.NUDGE;
            this.onAnimationStart(tween, reverse);
        });

        return tween;
    }

    public forcePositionUpdate() {
        this.updatePosition();
    }

    public fillUp(symbolNames: string[], symLandCallback: AvalancheSymbolLandCb): gsap.core.Timeline {
        let popped: SimpleReelModelSymbolData[] = [];
        const unLockedScatters: number = 0;
        let symbolsBeforeNearWin: SimpleReelModelSymbolData[] = [];
        const paddingCountBefore: number = this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore;
        const paddingBefore: SimpleReelModelSymbolData[] = this._symbols.splice(0, paddingCountBefore);
        const visibleSymbols: SimpleReelModelSymbolData[] = this._symbols.splice(0, this.paddingConfig.visibleSymbols);
        const paddingAfter: SimpleReelModelSymbolData[] = this._symbols;

        const localNewSyms: SimpleReelModelSymbolData[] = [];
        for (let j: number = visibleSymbols.length - 1; j >= 0; j--) {
            const symbol: SimpleReelModelSymbolData = visibleSymbols[j];
            if (symbol.removed) {
                const spliced: SimpleReelModelSymbolData[] = visibleSymbols.splice(j, 1);
                popped.unshift(spliced[0]);
                const newSymbolName: string = symbolNames[symbol.reelPosition + this.paddingConfig.paddingBefore];
                const newSymbol: SimpleReelModelSymbolData = this.createNewSymbol(newSymbolName, symbol.reelPosition, SimpleReelModelState.FILL_UP);
                newSymbol.positionable.position.x = 0;
                newSymbol.positionable.position.y = BS2Statics.STEP_HEIGHT * (symbol.reelPosition - 5);
                localNewSyms.push(newSymbol);

                if (unLockedScatters < 2) {
                    symbolsBeforeNearWin.push(newSymbol);
                }
            }
        }

        const blankCount: number = localNewSyms.length;


        for (let newSym of localNewSyms) {
            visibleSymbols.unshift(newSym);
        }


        this._symbols = paddingBefore.concat(visibleSymbols.concat(paddingAfter));


        const tl: gsap.core.Timeline = gsap.timeline();
        tl.add(() => {
            this._symbols.forEach((sym) => {
                (<BS2Symbol>sym.positionable).symbolPosition = {
                    reelID: sym.reelId,
                    position: sym.reelPosition
                }
            })
        })
        tl.add(this.animateNew(symbolsBeforeNearWin, BS2Statics.FILL_UP_DROP_DURATION, 0.0, BS2Statics.FILL_UP_SYMBOL_DELAY, SimpleSymbolState.DROP_IN, symLandCallback, {
            isNearWin: false,
            isSingleSymbolDrop: true
        }, blankCount));

        tl.add(() => {
            this.clearSymbols(popped);
        })
        return tl;
    }

    public collapse(symLandCallback: AvalancheSymbolLandCb): gsap.core.Timeline {
        let localPop: SimpleReelModelSymbolData[] = [];

        const paddingCountBefore: number = this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore,
            paddingBefore: SimpleReelModelSymbolData[] = this._symbols.splice(0, paddingCountBefore),
            visibleSymbols: SimpleReelModelSymbolData[] = this._symbols.splice(0, this.paddingConfig.visibleSymbols),
            paddingAfter: SimpleReelModelSymbolData[] = this._symbols;

        for (let i: number = visibleSymbols.length - 1; i >= 0; i--) {
            const symbol: SimpleReelModelSymbolData = visibleSymbols[i];
            if (symbol.removed) {
                const spliced: SimpleReelModelSymbolData[] = visibleSymbols.splice(i, 1);
                localPop.unshift(spliced[0]);
            }
        }

        for (let sym of localPop) {
            visibleSymbols.unshift(sym);
        }


        const tl: gsap.core.Timeline = this.animate(visibleSymbols, BS2Statics.COLLAPSE_DURATION, BS2Statics.COLLAPSE_DELAY, SimpleSymbolState.COLLAPSE_L2R, true, symLandCallback, {
            isNearWin: false,
            isSingleSymbolDrop: true
        },);

        this._symbols = paddingBefore.concat(visibleSymbols.concat(paddingAfter));

        return tl;

    }

    public animate(symbols: (SimpleReelModelSymbolData | null)[], dropDuration: number, delay: number, state: SimpleSymbolState, reverse: boolean, symLandCallback: AvalancheSymbolLandCb, callbackData: SymbolStopCallBackData,): gsap.core.Timeline {
        let drops: gsap.core.Timeline[] = [];

        for (let j: number = 0; j < symbols.length; j++) {
            const symbol: SimpleReelModelSymbolData | null = symbols[j];

            if (symbol === undefined) {
                continue;
            }
            if (symbol!.reelPosition == j) {
                //Is in it's right pos
                continue;
            }

            symbol!.reelPosition = j;

            if (symbol!.removed) {
                continue;
            }

            const dropTl: gsap.core.Timeline = gsap.timeline();
            dropTl.add(() => {
                symbol!.positionable.setAvalancheState(state);
            })
            dropTl.add(gsap.to(symbol!.positionable.position, {
                duration: dropDuration,
                x: symbol!.reelPosition * this.stepSize.x,
                y: symbol!.reelPosition * this.stepSize.y,
                ease: "power1.in"
            }));
            dropTl.add(() => {
                symbol!.positionable.setAvalancheState(SimpleSymbolState.NORMAL);
            })
            dropTl.add(() => symLandCallback(symbol!, callbackData));

            drops.push(dropTl);
        }


        if (reverse) {
            drops = drops.reverse();
        }

        return GSAPUtils.createTimelineFromArray(drops, `<${delay}`);
    }

    //Public utils
    public replaceWithNewSymbol(index: number, newName?: string): replaceWithNewSymbolDataType {
        newName = newName != undefined ? newName : this.getRandomSymbolVisualPaddingSymbolName();
        const oldSymbol: SimpleReelModelSymbolData = this._symbols[index];
        const newSymbol: SimpleReelModelSymbolData = this.createNewSymbol(newName, oldSymbol.reelPosition, SimpleReelModelState.IDLE);
        newSymbol.positionable.position.x = oldSymbol.positionable.position.x;
        newSymbol.positionable.position.y = oldSymbol.positionable.position.y;

        this._symbols[index] = newSymbol;

        return {
            oldSymbol: oldSymbol,
            newSymbol: newSymbol
        };
    }


    //Public functions

    public swapSymbol(oldSymbol: SimpleReelModelSymbolData, newSymbol: SimpleReelModelSymbolData) {
        this._symbols[this.visibleIndexToRealIndex(oldSymbol.reelPosition)] = newSymbol;
        this._delegate.removeSymbol(oldSymbol);

    }

    public updatePaddingConfig(config: SimpleReelPaddingData) {

        const totalAfter: number = config.visualPaddingBefore + config.paddingBefore + config.visibleSymbols + config.visualPaddingAfter + config.paddingAfter;

        const oldBeforePadding: number = this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore;
        const newBeforePadding: number = config.visualPaddingBefore + config.paddingBefore;
        let beforeDiff: number = oldBeforePadding - newBeforePadding;


        if (beforeDiff > 0) {
            //If more padding than wanted, remove them
            const removedSymbols = this.symbols.splice(0, beforeDiff);
            for (let sym of removedSymbols) {
                this._delegate.removeSymbol(sym);
            }
        } else {
            //If less, add new padding symbols
            beforeDiff = Math.abs(beforeDiff);
            const newSymbols = [];
            for (let i: number = 0; i < beforeDiff; i++) {
                newSymbols.push(this.createNewSymbol(this.getRandomSymbolVisualPaddingSymbolName(), 0, SimpleReelModelState.IDLE));
            }
            this.symbols.unshift(...newSymbols);
        }


        let totalDiff = this.symbols.length - totalAfter;
        if (totalDiff > 0) {
            const removedSymbols = this.symbols.splice(-totalDiff, totalDiff);
            for (let sym of removedSymbols) {
                this._delegate.removeSymbol(sym);
            }
        } else {
            //If less, add new padding symbols
            totalDiff = Math.abs(totalDiff);
            const newSymbols = [];
            for (let i: number = 0; i < totalDiff; i++) {
                newSymbols.push(this.createNewSymbol(this.getRandomSymbolVisualPaddingSymbolName(), 0, SimpleReelModelState.IDLE));
            }
            this.symbols.push(...newSymbols);
        }


        this.paddingConfig.visualPaddingBefore = config.visualPaddingBefore;
        this.paddingConfig.paddingBefore = config.paddingBefore;
        this.paddingConfig.visibleSymbols = config.visibleSymbols;
        this.paddingConfig.paddingAfter = config.paddingAfter;
        this.paddingConfig.visualPaddingAfter = config.visualPaddingAfter;


        for (let i: number = 0; i < this.symbols.length; i++) {
            this.symbols[i].reelPosition = i - this.paddingConfig.visualPaddingBefore - this.paddingConfig.paddingBefore;
        }
        this.updatePosition();

    }

    public visibleIndexToRealIndex(visibleIndex: number) {
        const paddingBefore: number = this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore;
        return visibleIndex + paddingBefore;
    }

    public getAllVisibleSymbols(): SimpleReelModelSymbolData [] {
        const paddingBefore: number = this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore;
        if (this._stopSymbols.length > 0) {
            return this._stopSymbols.slice(paddingBefore, paddingBefore + this.paddingConfig.visibleSymbols);
        } else {
            return this._symbols.slice(paddingBefore, paddingBefore + this.paddingConfig.visibleSymbols);
        }
    }

    public getVisibleSymbolAt(symbolIndex: number): SimpleReelModelSymbolData {
        const index: number = this.visibleIndexToRealIndex(symbolIndex);

        if (this._stopSymbols.length > 0) {
            return this._stopSymbols[index];
        } else {
            return this._symbols[index];
        }
    }

    public getDataForPositionable(positionable: Positionable): SimpleReelModelSymbolData | undefined {
        const symbols = this.getAllVisibleSymbols();
        for (let symbolData of symbols) {
            if (symbolData.positionable == positionable) {
                return symbolData;
            }
        }

        return;
    }

    public unshiftRandomSymbol() {
        const symbol = this.createNewSymbol(this.getRandomSymbolVisualPaddingSymbolName(), this._symbols[0].reelPosition - 1, SimpleReelModelState.IDLE);
        this.updatePositionForSymbol(symbol);
        this._symbols.unshift(symbol);
    }

    private getNextSymbolName(nextSymbolPosition: number): string {
        let nextIndex = this._reelSetIndex + nextSymbolPosition;
        if (nextIndex < 0) {
            nextIndex = this.reelSet.length + nextIndex;
        }
        if (nextIndex >= this.reelSet.length) {
            nextIndex = nextIndex - this.reelSet.length;
        }
        return this.reelSet[nextIndex];

    }

    private getNextSymbol(position: number, state: SimpleReelModelState): SimpleReelModelSymbolData {
        //Stopping
        if (this._stopping && this._stopSymbols.length > 0) {
            const symbol = this._stopSymbols[this._stopIndex];
            symbol.reelPosition = position;
            return symbol;
        }
        //Default
        return this.createNewSymbol(this.getNextSymbolName(position), position, state)
    }

    private getRandomSymbolVisualPaddingSymbolName(): string {
        const randomIndex: number = Math.floor(Math.random() * this.reelSet.length);
        let symbol: string = this.reelSet[randomIndex];
        if (this.illegalVisualPaddingSymbols.indexOf(symbol) >= 0) {
            symbol = this.getRandomSymbolVisualPaddingSymbolName();
        }
        return symbol;
    }

    private getRandomSymbolInReelSet(): string {
        const randomIndex = Math.floor(Math.random() * this.reelSet.length);
        return this.reelSet[randomIndex];
    }

    private getRandomSymbols(count: number): string[] {
        const result: string[] = [];
        for (let i: number = 0; i < count; i++) {
            result.push(this.getRandomSymbolVisualPaddingSymbolName());
        }
        return result;
    }

    private startSpin(reverse: boolean = false) {
        this._spinning = true;
        this.state = SimpleReelModelState.SPIN;
        this.masterSpinTimeline = gsap.timeline();
        this.masterSpinTimeline.add(this.spin(reverse));
    }

    private spin(reverse: boolean = false): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline({
            onComplete: () => {
                if (this._spinning) {
                    this.masterSpinTimeline?.add(this.spin(reverse));
                    //this.spin(reverse);
                }
            }
        })
        tl.add(this.step(reverse));
        return tl;
    }

    private step(reverse: boolean = false): gsap.core.Tween {
        const tween: gsap.core.Tween = gsap.to(this.animatedStepData, {
            duration: this.stepDuration,
            x: reverse ? -this.stepSize.x : this.stepSize.x,
            y: reverse ? -this.stepSize.y : this.stepSize.y,
            ease: "none",
            onStart: () => this.onAnimationStart(tween, reverse),
            onUpdate: () => this.updatePosition(),
            onComplete: () => this.onAnimationComplete(reverse)
        });
        return tween;
    }

    private onAnimationStart(tween: gsap.core.Tween, reverse: boolean = false) {
        this._currentSpinStep = tween;
        this.addSymbol(reverse);
    }

    private onAnimationComplete(reverse: boolean = false) {
        for (let i: number = 0; i < this._symbols.length; i++) {
            const symbol = this._symbols[i];
            symbol.reelPosition += reverse ? -1 : 1;
        }

        this.animatedStepData.x = 0;
        this.animatedStepData.y = 0;

        if (this.triggerStop) {
            this.triggerStop = false;
            this.masterSpinTimeline?.kill();
            this._stopTimeline?.play(0);
        }

        this.updateIndex(reverse);
        this.removeSymbol();
        this.updatePosition();
    }

    private updateIndex(reverse: boolean) {
        const valueChange = reverse ? 1 : -1
        if (this._stopping) {
            this.setStopIndex(this._stopIndex + valueChange);
        } else {
            this.setReelIndex(this._reelSetIndex + valueChange);
        }
    }

    private setStopIndex(value: number) {
        if (value >= this._stopSymbols.length) {
            value = 0;
        } else if (value < 0) {
            value = this._stopSymbols.length - 1;
        }
        this._stopIndex = value;
    }

    private setReelIndex(value: number) {
        if (value >= this.reelSet.length) {
            value = 0;
        } else if (value < 0) {
            value = this.reelSet.length - 1;
        }
        this._reelSetIndex = value;
    }

    private addSymbol(reverse: boolean) {
        let newSymbols = [];
        let newSymbolPosition = this._symbols[0].reelPosition - 1;

        if (reverse) {
            newSymbolPosition = this._symbols[this._symbols.length - 1].reelPosition + 1;
        }

        newSymbols.push(this.getNextSymbol(newSymbolPosition, this.state));

        this._symbols = this._symbols.concat(newSymbols);
        this._symbols.sort((a: any, b: any) => {
            return (a.reelPosition < b.reelPosition) ? -1 : 1;
        });
    }

    private removeSymbol() {
        for (let i: number = this._symbols.length - 1; i >= 0; i--) {
            const symbol = this._symbols[i];
            if (symbol.reelPosition >= this.paddingConfig.visibleSymbols + this.paddingConfig.paddingAfter + this.paddingConfig.visualPaddingAfter) {
                //Remove from bottom
                const removedSymbol = this._symbols.splice(i, 1)[0];
                this._delegate.removeSymbol(removedSymbol);


            }
            if (symbol.reelPosition < -this.paddingConfig.paddingBefore - this.paddingConfig.visualPaddingBefore) {
                //Remove from top
                const removedSymbol = this._symbols.splice(i, 1)[0];
                this._delegate.removeSymbol(removedSymbol);

            }
        }
    }

    private updatePosition() {
        for (let i: number = 0; i < this._symbols.length; i++) {
            this.updatePositionForSymbol(this._symbols[i]);
        }
    }

    private updatePositionForSymbol(symbol: SimpleReelModelSymbolData) {
        /*  const basePosX = this.stepSize.x * symbol.reelPosition;
          symbol.positionable.position.x = basePosX + this.animatedStepData.x;*/

        const basePosY = this.stepSize.y * symbol.reelPosition;
        symbol.positionable.position.y = basePosY + this.animatedStepData.y;
    }

    private animateNew(symbols: SimpleReelModelSymbolData[], dropDuration: number, rowDelay: number, symdelay: number, state: SimpleSymbolState, symLandCallback: AvalancheSymbolLandCb, callbackData: SymbolStopCallBackData, blankCount: number): gsap.core.Timeline {
        const rows: gsap.core.Timeline[] = [];
        const drops: gsap.core.Timeline[] = [];
        var isFirstDagger:boolean = true;
        var isFirstGuillotine:boolean = true;
        var isFirstWild:boolean = true;
        for (let j: number = 0; j < symbols.length; j++) {
            const symbol = symbols[j];

            const dropTl = gsap.timeline();
            dropTl.add(() => {
                symbol.positionable.setAvalancheState(state);
            })
            dropTl.add(gsap.to(symbol.positionable.position, {
                duration: dropDuration * blankCount,
                x: 0,
                y: symbol.reelPosition * BS2Statics.STEP_HEIGHT,
                ease: "power1.in"
            }));
            dropTl.add(() => {
                symbol.positionable.setAvalancheState(SimpleSymbolState.NORMAL);
            })
            dropTl.add(() => symLandCallback(symbol, callbackData));
            if(symbol.name === BS2Statics.SYMBOLS.D && isFirstDagger) {
                dropTl.add(()=>{BS2.soundPlayer.playSound(BS2Sound.D_LAND)},"<");
                isFirstDagger = false;
            }
            if(symbol.name === BS2Statics.SYMBOLS.G && isFirstGuillotine){
                dropTl.add(()=>{BS2.soundPlayer.playSound(BS2Sound.G_LAND)},"<");
                isFirstGuillotine = false;
            }
            if(symbol.name === BS2Statics.SYMBOLS.W && isFirstWild){
                dropTl.add(()=>{BS2.soundPlayer.playWildLandingSound(symbol.reelId)},"<");
                isFirstWild = false;
            }
            drops.push(dropTl);
        }


        if (drops.length > 0) {
            rows.push(GSAPUtils.createTimelineFromArray(drops, `<${symdelay}`))
        }


        return GSAPUtils.createTimelineFromArray(rows, `<${rowDelay}`);
    }

    get reelSet(): string[] {
        if (!this._reelSet) {
            throw new Error(`No reelset set for reel: ${this.id}`);
        }
        return this._reelSet;
    }

    get stopTriggerTl(): gsap.core.Timeline | undefined {
        return this._stopTriggerTl;
    }

    get stopTimeline(): gsap.core.Timeline | undefined {
        return this._stopTimeline;
    }

    get speed(): number {
        return this._speed;
    }

    set speed(value: number) {
        this._speed = value;
        this.animationTimeScale = value * this.stepDuration;
        this.masterSpinTimeline?.timeScale(this.animationTimeScale);
    }

    get state(): SimpleReelModelState {
        return this._state;
    }

    set state(value: SimpleReelModelState) {
        this._state = value;
        if (this.events) {
            this.events.trigger("ReelStateChange", <SimpleReelModelEventData>{reel: this.id, state: this._state});
        }
    }

    get symbols(): SimpleReelModelSymbolData[] {
        return this._symbols;
    }

}